import { resolve } from "videojs-contrib-dash";

export default class DrmDetector {
  constructor() {
    this._config = [{
      'initDataTypes': ['cenc'],
      'videoCapabilities': [{ 'contentType': 'video/mp4;codecs="avc1.4D401E"' }]
    }];
  }

  config() {
    return this._config;
  }

  static get keySystems() {
    return ['com.microsoft.playready',
      'com.widevine.alpha',
      'com.apple.fairplay',
      'com.apple.fps'];
  }

  async drmCheck(keysystem) {
    try {
      return await navigator.requestMediaKeySystemAccess(keysystem, this.config());
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  async hdcpCheck(mediaKeySystemAccess, hdcpLevel) {
    try {
      const mediaKeys = await this.createMediaKeys(mediaKeySystemAccess);
      const hdcp_status = await this.checkMediaKeys(mediaKeys, hdcpLevel);
      return this.checkHDCPStatus(hdcp_status);
    } catch (e) {
      console.warn(e);
      return Promise.resolve('unsupported');
    }
  }

  createMediaKeys(mediaKeySystemAccess) {
    return mediaKeySystemAccess.createMediaKeys();
  }

  checkMediaKeys(mediaKeys, hdcpLevel) {
    if (!('getStatusForPolicy' in mediaKeys)) {
      return Promise.resolve('unsupported');
    }

    return mediaKeys.getStatusForPolicy(hdcpLevel);
  }

  checkHDCPStatus(hdcp_status) {
    if (hdcp_status !== 'usable') {
      return Promise.resolve('unsupported');
    }
    return Promise.resolve('supported');
  }

  async widevineHdcpCheck() {
    return this.drmCheck('com.widevine.alpha').then((response) => {
      if (response) {
        return this.hdcpCheck(response, { minHdcpVersion: '1.0' });
      } else {
        return Promise.reject();
      }
    });
  }

  static async getSupportedDrm() {
    var wvKeySysConfig = [{
      "initDataTypes": ["cenc"],
      "videoCapabilities": [
        {
          "contentType": "video/mp4;codecs=\"avc1.4D401E\"",
          "robustness": "SW_SECURE_CRYPTO"
        },
        {
          "contentType": "video/mp4;codecs=\"avc1.4D401E\"",
          "robustness": "SW_SECURE_DECODE"
        },
        {
          "contentType": "video/mp4;codecs=\"avc1.4D401E\"",
          "robustness": "HW_SECURE_CRYPTO"
        },
        {
          "contentType": "video/mp4;codecs=\"avc1.4D401E\"",
          "robustness": "HW_SECURE_DECODE"
        },
        {
          "contentType": "video/mp4;codecs=\"avc1.4D401E\"",
          "robustness": "HW_SECURE_ALL"
        }
      ]
    }];

    var prKeySysConfig = [{
      "initDataTypes": ["cenc"],
      "videoCapabilities": [
        {
          "contentType": "video/mp4;codecs=\"avc1.4D401E\"",
          "robustness": "150"
        },
        {
          "contentType": "video/mp4;codecs=\"avc1.4D401E\"",
          "robustness": "2000"
        },
        {
          "contentType": "video/mp4;codecs=\"avc1.4D401E\"",
          "robustness": "3000"
        }
      ],
      distinctiveIdentifier: "required",
      persistentState: "required"
    }];

    var keySystems = {
      playready: ['com.microsoft.playready.recommendation', 'com.microsoft.playready.recommendation.3000'],
      widevine: ['com.widevine.alpha']
    };

    const supportedDrm = {
      widevine: [],
      playready: [],
      fairplay: []
    }

    for (const keySystem in keySystems) {
      for (const idx in keySystems[keySystem]) {
        let keySys = keySystems[keySystem][idx];
        try {
          let keySysConfig = wvKeySysConfig
          if (keySystem === 'playready') {
            keySysConfig = prKeySysConfig
          }
          await navigator.requestMediaKeySystemAccess(keySys, keySysConfig).
            then(mediaKeySystemAccess => {
              const robustness = DrmDetector.checkDrmRobustness(keySystem, mediaKeySystemAccess);
              if (robustness.length > 0) {
                supportedDrm[keySystem] = robustness;
              }
            }).catch(ex => { });
        } catch (ex) { }
      }
    }

    supportedDrm["fairplay"] = DrmDetector.fairplayChecking();

    return supportedDrm;
  }

  static checkDrmRobustness(type, keyAccess) {
    if (type === 'widevine') {
      return DrmDetector.widevineLevelChecking(keyAccess)
    } else if (type === 'playready') {
      return DrmDetector.playreadyChecking(keyAccess)
    } else if (type === 'fairplay') {
      return DrmDetector.fairplayChecking()
    }
  }

  static widevineLevelChecking(mediaKeySystemAccess) {
    const widevineConf = mediaKeySystemAccess.getConfiguration();
    const l3 = widevineConf.videoCapabilities.filter((i) => { return (i["robustness"] === "SW_SECURE_CRYPTO" || i["robustness"] === "SW_SECURE_DECODE") });
    const l2 = widevineConf.videoCapabilities.filter((i) => { return i["robustness"] === "HW_SECURE_CRYPTO" });
    const l1 = widevineConf.videoCapabilities.filter((i) => { return (i["robustness"] === "HW_SECURE_DECODE") || (i["robustness"] === "HW_SECURE_ALL") });
    var supportedLevel = [];
    if (l3.length > 0) {
      supportedLevel.push("L3");
    }
    if (l2.length > 0) {
      supportedLevel.push("L2");
    }
    if (l1.length > 0) {
      supportedLevel.push("L1");
    }
    return supportedLevel;
  };

  static playreadyChecking(mediaKeySystemAccess) {
    if (mediaKeySystemAccess.keySystem === 'com.microsoft.playready.recommendation') {
      return ['SL2000', 'SL150']
    } else if (mediaKeySystemAccess.keySystem === 'com.microsoft.playready.recommendation.3000') {
      return ['SL3000', 'SL2000', 'SL150']
    }
    return []
  }

  static fairplayChecking() {
    if (typeof window.WebKitMediaKeys === 'function') {
      return ["1"];
    } else {
      return [];
    }
  }
}
